import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SETTINGS } from '../../../../core/configs/common.settings';
import { catchError, Observable, Subject, tap, throwError } from 'rxjs';
import { EncryptionService } from '../../../../core/services/encryption.service';
import { Router } from '@angular/router';
// Do not import AuthGuard (or any guard that imports AuthService) — that cycle breaks lazy UserModule init.
import { UserService } from '../../../../core/services/user.service';
import { clear, getItem, removeItem, setItem } from '../../../../core/utils/safe-storage';
import { PermissionService } from '../../../../core/services/permission.service';
import { RoleStorageService } from '../../../../core/services/role-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loginStatus = new Subject();

  constructor(
    private http: HttpClient,
    private encryption: EncryptionService,
    private router: Router,
    private userService: UserService,
    private permissionService: PermissionService,
    private roleStorage: RoleStorageService
  ) {}

  private storeUserRole(roleName: string): void {
    if (!roleName) {
      return;
    }
    this.roleStorage.setRole(roleName);
    this.permissionService.notifyPermissionsChanged();
    this.permissionService.loadEffectiveFromServer();
  }

  /** Keep USER_ROLE in storage aligned with API profile (e.g. after refresh or older sessions). */
  syncRoleFromUser(user: { role?: { name?: string } } | null | undefined): void {
    const roleName = user?.role?.name;
    if (roleName) {
      this.storeUserRole(roleName);
    }
  }

  login(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      // Drop any stale session so login 403 is not mistaken for JWT expiry refresh.
      this.permissionService.clearServerEffective();
      this.clearStoredRole();
      removeItem(SETTINGS.ACCESS_TOKEN);
      removeItem(SETTINGS.REFRESH_TOKEN);
      removeItem(SETTINGS.LOGGED_IN_USER);
      removeItem(SETTINGS.LOGGED_IN_USER_ID);

      this.http.post(`${SETTINGS.BASE_API}/user/login`, payload).subscribe({
        next: (response: any) => {
          this.setSession(response);
          resolve(response);
          setItem(SETTINGS.LOGGED_IN_USER_ID, response.user.userId);
          setItem(SETTINGS.COMPANY, '');
          setItem(SETTINGS.COMPANY_NAME, '');
          if (!this.isLoggedIn()) {
            clear();
            this.router.navigate(['/auth/login']);
            return;
          }
          this.resolveRoleAndNavigate(response);
        },
        error: (error: any) => {
          if (error.status === 403 || error === 403) {
            this.loginStatus.next({ isAuthenticated: false });
          }
          const bodyMsg =
            typeof error?.error === 'string'
              ? error.error
              : error?.error?.message;
          const msg =
            (typeof bodyMsg === 'string' && bodyMsg.trim() && bodyMsg) ||
            (typeof error?.message === 'string' &&
            !/^Http failure response/i.test(error.message)
              ? error.message
              : null) ||
            'Authentication failed. Please check your email and password.';
          reject(msg);
        },
      });
    });
  }

  /** Set USER_ROLE from response or fetch user details, then navigate. STAFF -> access-denied. */
  private resolveRoleAndNavigate(loginResponse: any): void {
    const roleName = loginResponse?.user?.role?.name;
    if (roleName) {
      this.storeUserRole(roleName);
      this.navigateByRole(roleName);
      return;
    }
    const userId = getItem(SETTINGS.LOGGED_IN_USER_ID);
    if (!userId) {
      this.router.navigate(['/auth/login']);
      return;
    }
    this.userService.userDetailsById(userId).subscribe({
      next: (user: any) => {
        const r = user?.role?.name || '';
        this.storeUserRole(r);
        this.navigateByRole(r);
      },
      error: () => {
        this.router.navigate(['/app/dashboard']);
      },
    });
  }

  private navigateByRole(roleName: string): void {
    if (roleName === 'STAFF') {
      this.router.navigate(['/auth/access-denied']);
    } else {
      this.router.navigate(['/app/dashboard']);
    }
  }

  public isLoggedIn(): any {
    if (
      getItem(SETTINGS.ACCESS_TOKEN) != null &&
      getItem(SETTINGS.REFRESH_TOKEN) != null
    ) {
      const tokenENC: any = getItem(SETTINGS.ACCESS_TOKEN);
      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        this.logout();
      }
    }
    return false;
  }

  logout(): void {
    this.http.post(`${SETTINGS.BASE_API}/user/logout`, {}).subscribe({
      next: () => {
        this.permissionService.clearServerEffective();
        this.clearStoredRole();
        clear();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.permissionService.clearServerEffective();
        this.clearStoredRole();
        clear();
        this.router.navigate(['/auth/login']);
      },
    });
  }

  /** Clear stored role (call on logout so next login re-resolves). */
  clearStoredRole(): void {
    this.roleStorage.clearRole();
  }

  private setSession(authResult: any): void {
    const { access_token, refresh_token, user } = authResult;
    this.setLoggedInUser(JSON.stringify(user));
    setItem(SETTINGS.ACCESS_TOKEN, this.encryption.encrypt(access_token));
    setItem(SETTINGS.REFRESH_TOKEN, this.encryption.encrypt(refresh_token));
    this.loginStatus.next({ isAuthenticated: true });
  }

  setLoggedInUser(userStr: any): void {
    setItem(SETTINGS.LOGGED_IN_USER, this.encryption.encrypt(userStr));
  }

  changePwd(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.BASE_API}/user/change-password`, payload)
        .subscribe({
          next: (response: any) => {
            this.setSession(response);
            resolve(response);
            this.router.navigate(['/auth/login']);
          },
          error: (error: any) => {
            if (error.status === 403 || error === 403) {
              this.loginStatus.next({ isAuthenticated: false });
            }
            reject(error.error.message);
          },
        });
    });
  }

  getToken(): any {
    if (getItem(SETTINGS.ACCESS_TOKEN) != null) {
      const tokenENC: any = getItem(SETTINGS.ACCESS_TOKEN);

      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        return '';
      }
    }
    return false;
  }

  refreshToken(): Observable<any> {
    return this.http
      .post(`${SETTINGS.BASE_API}/user/refresh-token`, {
        refreshToken: this.getRefreshToken(),
      })
      .pipe(
        tap((res: any) => {
          setItem(
            SETTINGS.ACCESS_TOKEN,
            this.encryption.encrypt(res.access_token)
          );
        }),
        catchError(err => {
          clear();
          this.router.navigate(['/auth/login']);
          return throwError(() => err);
        })
      );
  }

  getRefreshToken(): any {
    if (getItem(SETTINGS.REFRESH_TOKEN) != null) {
      const refreshTokenENC: any = getItem(SETTINGS.REFRESH_TOKEN);

      try {
        return this.encryption.decrypt(refreshTokenENC);
      } catch (e) {
        return null;
      }
    }
    return null;
  }
}
